import React, { useState, useEffect } from 'react';
import { houseInfo } from '../services/supabaseClient';
import { fetchCalendarData, formatDateForAPI } from '../services/bookingAPI';

const Calendar = ({ onDateClick, currentMonth, onMonthChange }) => {
  const [calendarData, setCalendarData] = useState({});
  const [loading, setLoading] = useState(true);

  // Generate calendar dates in a horizontal line (no grid)
  const generateCalendarDates = (year, month) => {
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const dates = [];
    
    // Add all days of the month in a line
    for (let day = 1; day <= daysInMonth; day++) {
      dates.push(new Date(year, month, day));
    }
    
    return dates;
  };

  // Fetch calendar data when month changes
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const startDate = formatDateForAPI(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1));
      const endDate = formatDateForAPI(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0)); // Just current month
      
      console.log('Fetching calendar data for:', startDate, 'to', endDate);
      const data = await fetchCalendarData(startDate, endDate);
      console.log('Calendar data received:', data);
      setCalendarData(data);
      setLoading(false);
    };

    fetchData();
  }, [currentMonth]);

  // Check if a date is available for a specific house
  const isDateAvailable = (houseKey, date) => {
    if (!date || !calendarData[houseKey]) return false;
    
    // Don't show past dates as available
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date < today) return false;
    
    const dateStr = formatDateForAPI(date);
    // Check if there are any available bookings for this date
    return calendarData[houseKey].some(booking => 
      booking.check_in_date === dateStr && booking.status === 'available'
    );
  };

  // Get available stay types for a specific date and house
  const getAvailableStayTypes = (houseKey, date) => {
    if (!date || !calendarData[houseKey]) return [];
    
    const dateStr = formatDateForAPI(date);
    const availableBookings = calendarData[houseKey].filter(booking => 
      booking.check_in_date === dateStr && booking.status === 'available'
    );
    
    return availableBookings.map(booking => booking.stay_type);
  };

  // Handle date click
  const handleDateClick = (date, houseKey) => {
    if (date && isDateAvailable(houseKey, date)) {
      const availableStayTypes = getAvailableStayTypes(houseKey, date);
      onDateClick(date, houseKey, availableStayTypes);
    }
  };

  // Navigate months
  const navigateMonth = (direction) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(currentMonth.getMonth() + direction);
    onMonthChange(newMonth);
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Generate dates for current month only
  const calendarDates = generateCalendarDates(currentMonth.getFullYear(), currentMonth.getMonth());
  const dayNames = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  if (loading) {
    return <div className="calendar-loading">Loading calendar...</div>;
  }

  return (
    <div className="calendar-container">
      <div className="calendar-header">
        <button className="nav-btn" onClick={() => navigateMonth(-1)}>‹</button>
        <div className="month-selector">
          <span className="current-month">
            {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
          </span>
        </div>
        <button className="nav-btn" onClick={() => navigateMonth(1)}>›</button>
      </div>

      <div className="calendar-grid">
        <div className="property-column">
          <div className="property-header">Property Name (Capacity)</div>
          {Object.entries(houseInfo).map(([houseKey, info]) => (
            <div key={houseKey} className="property-row">
              <span className="property-name">{info.name}</span>
              <span className="property-capacity">({info.capacity})</span>
            </div>
          ))}
        </div>

        <div className="dates-grid">
          <div className="day-headers">
            {calendarDates.map((date, index) => (
              <div key={index} className="day-header">
                {dayNames[date.getDay()]}
              </div>
            ))}
          </div>

          {Object.entries(houseInfo).map(([houseKey, info]) => (
            <div key={houseKey} className="house-row">
              {calendarDates.map((date, index) => (
                <div 
                  key={index} 
                  className={`date-cell ${
                    date && isDateAvailable(houseKey, date) ? 'available' : ''
                  }`}
                  onClick={() => handleDateClick(date, houseKey)}
                >
                  <span className={`date-number ${isDateAvailable(houseKey, date) ? 'available-date' : ''}`}>
                    {date.getDate()}
                  </span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      <div className="calendar-footer">
        <button className="select-date-btn">Select start date</button>
        <div className="calendar-legend">
          <div className="legend-item">
            <div className="legend-key">
              <span className="available-indicator"></span>
              <span>Available date</span>
            </div>
            <div className="legend-key">
              <span className="unavailable-indicator"></span>
              <span>Unavailable date</span>
            </div>
          </div>
          <div className="booking-info">
            <p>Check-in is 4pm either a <strong>Friday</strong> or <strong>Monday</strong> only.</p>
            <p>Check-out 10am. 3 night minimum at weekends.</p>
            <p>Terms and conditions apply to all bookings.</p>
            <p>Bookings are provisional until we confirm.</p>
          </div>
        </div>
        <div className="cookie-policy">Cookie policy</div>
      </div>
    </div>
  );
};

export default Calendar;
